import type {PublicAccessPass, PublicPassOffer, PublicPassPromotion, PublicPricingClass} from '../../../services/public-passes.service'

/** What a visitor pays for one entry to a class, as the price list shows it. */
export type ClassEntryPrice =
    | {kind: 'paid'; priceMinor: number; promotion: ShownPromotion | null}
    | {kind: 'free'}
    | {kind: 'passes'}

/**
 * A reduction that may be shown: it always carries its Omnibus reference (ADR 0015).
 * `firstClassOnly` for a new-customer price limited to the first class rather than the first purchase.
 */
type ShownPromotion = {name: string; finalAmountMinor: number; lowestPrice30dMinor: number; firstClassOnly: boolean}

function shownPromotion(promotion: PublicPassPromotion | null | undefined): ShownPromotion | null {
    return promotion && typeof promotion.lowestPrice30dMinor === 'number'
        ? {
            name: promotion.name ?? '',
            finalAmountMinor: promotion.finalAmountMinor,
            lowestPrice30dMinor: promotion.lowestPrice30dMinor,
            firstClassOnly: promotion.eligibility === 'FIRST_CLASS_PURCHASE',
        }
        : null
}

/** A class with an Entry Mode never lists public single-entry passes (ADR 0019). */
export function classPasses(pricingClass: PublicPricingClass, passesById: Map<string, PublicAccessPass>): PublicAccessPass[] {
    const acceptedIds = new Set(pricingClass.acceptedPassIds)
    // The public catalog is already ordered by the studio. Membership must not reorder it.
    return [...passesById.values()]
        .filter((pass) => acceptedIds.has(pass.id))
        .filter((pass) => (pricingClass.entryMode === null || pricingClass.entryMode === undefined) || !isSingleEntryPass(pass))
}

export function classEntryPrice(pricingClass: PublicPricingClass): ClassEntryPrice {
    if (pricingClass.entryMode === 'free') return {kind: 'free'}
    if (pricingClass.entryMode === 'paid' && typeof pricingClass.priceMinor === 'number') {
        const promotion = shownPromotion(pricingClass.automaticPromotion)
        return {
            kind: 'paid',
            priceMinor: promotion?.finalAmountMinor ?? pricingClass.automaticPromotion?.finalAmountMinor ?? pricingClass.priceMinor,
            promotion,
        }
    }
    // Legacy classes sell access through pass products, including one-entry top-ups.
    // Their cheapest product is not an explicit single-entry price for the class.
    return {kind: 'passes'}
}

export function formatPassType(offer: Partial<Pick<PublicPassOffer, 'isBenefit' | 'type' | 'entriesTotal'>>, t: (key: string, params?: Record<string, string | number>) => string): string {
    if (offer.isBenefit) {
        return offer.type === 'entries'
            ? t('publicEntry.pricing.type.benefitEntries', { count: offer.entriesTotal ?? 0 })
            : t('publicEntry.pricing.type.benefit')
    }
    if (offer.type === 'one_time') return t('publicEntry.pricing.type.singleEntry')
    if (offer.type === 'entries') {
        return t('publicEntry.pricing.type.entries', { count: String(offer.entriesTotal ?? 0) })
    }
    return t('publicEntry.pricing.type.unlimited')
}

export function formatValidity(offer: Pick<PublicPassOffer, 'type' | 'validityDays'>, t: (key: string, params?: Record<string, string>) => string): string {
    if (offer.type === 'one_time') return t('publicEntry.pricing.validity.singleEntry')
    return typeof offer.validityDays === 'number'
        ? t('publicEntry.pricing.validity.days', { days: String(offer.validityDays) })
        : t('publicEntry.pricing.validity.studioOffer')
}

function isSingleEntryPass(pass: PublicAccessPass): boolean {
    return !pass.isBenefit && (pass.type === 'one_time' || pass.entriesTotal === 1)
}

export function uniquePricingClasses(classes: PublicPricingClass[]): PublicPricingClass[] {
  const unique = new Map<string, PublicPricingClass>();
  for (const item of classes) {
    const { id: _id, colorKey: _color, ...offer } = item;
    const key = JSON.stringify({
      ...offer,
      acceptedPassIds: [...offer.acceptedPassIds].sort(),
    });
    if (!unique.has(key)) unique.set(key, item);
  }
  return [...unique.values()]
}
